import { Component, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DecimalPipe, NgClass } from '@angular/common';

import { AdminEstimateNegotiationLine } from '../../../models/vendor-bills.model';
import { chargeTypeDisplayLabel } from '../../utils/charge-type-label.util';

export type NegotiationLineDecision = 'accept' | 'edit' | 'decline' | null;

/** One crew pill on a labor line: the vendor's value struck out (when it changed), the counter's value, and the unit after them. */
export interface CrewPart {
  label: string;
  orig: string | null;
  now: string;
  unit: string;
}

export interface NegotiationLineEditConfirmEvent {
  /** Cumulative man-hours for labor (techs × hoursPerTech), or the plain qty for non-labor lines. */
  qty: number;
  rate: number;
  value: number;
  /** Labor only: the crew size the admin entered. */
  techs?: number;
  /** Labor only: the per-tech hours the admin entered (qty = techs × hoursPerTech). */
  hoursPerTech?: number;
}

/**
 * One negotiation line's agent recommendation strip — gauge, delta, Accept/Edit/Decline/Message/Info.
 * Shared by the job-estimates-section table and the live-chat negotiate panel. Purely presentational:
 * decision/message/expand state lives in the parent, this component only renders it and emits intent.
 */
@Component({
  selector: 'app-estimate-negotiation-line',
  standalone: true,
  imports: [FormsModule, NgClass, DecimalPipe],
  templateUrl: './estimate-negotiation-line.component.html',
  styleUrl: './estimate-negotiation-line.component.scss',
})
export class EstimateNegotiationLineComponent {
  private static readonly GAUGE_RING_CIRCUMFERENCE = 97.4;

  line = input.required<AdminEstimateNegotiationLine>();
  decision = input<NegotiationLineDecision>(null);
  editedValue = input<number | null>(null);
  editedQty = input<number | null>(null);
  /** Labor only: a prior admin-entered crew size, to re-seed the editor. */
  editedTechs = input<number | null>(null);
  /** Labor only: a prior admin-entered per-tech hours, to re-seed the editor. */
  editedHoursPerTech = input<number | null>(null);
  message = input('');
  messageEdited = input(false);
  expandMode = input<'edit' | 'info' | 'msg' | null>(null);
  disabled = input(false);
  /** RFI's standard counter (AI unavailable): no AI confidence or AI wording on this line. */
  suggestedByRfi = input(false);
  /** Compact layout for the floating chat widget vs. full width in the estimates table. */
  compact = input(false);

  decisionChange = output<'accept'>();
  changeDecision = output<void>();
  editConfirm = output<NegotiationLineEditConfirmEvent>();
  expandModeChange = output<'edit' | 'info' | 'msg' | null>();
  messageChange = output<string>();
  messageReset = output<void>();

  editQtyDraft = signal<number | null>(null);
  editRateDraft = signal<number | null>(null);
  /** Labor only: crew size being edited. For labor, editQtyDraft holds PER-TECH hours. */
  editTechsDraft = signal<number | null>(null);

  readonly hasDecision = computed(() => this.decision() != null);

  /** A labor line shows techs + per-tech hours as two fields instead of a single qty. */
  readonly isLaborEdit = computed(() => this.line().isLabor === true);

  readonly displayValue = computed(() => {
    const line = this.line();
    if (!line.counterThisLine) return line.vendorValue;
    const decision = this.decision();
    if (decision === 'decline') return line.vendorValue;
    if (decision === 'edit') return this.editedValue() ?? line.suggestedValue;
    return line.suggestedValue;
  });

  /**
   * The struck-through "was" price: the vendor's FIRST submission (the saved original), not the vendor's latest edit, which
   * the estimate's own lines follow on every resubmit. Falls back to the current vendor value when no original was saved.
   * Display only: the delta, the counter and every decision still work on vendorValue.
   */
  readonly wasValue = computed(() => {
    const line = this.line();
    return line.originalValue ?? line.vendorValue;
  });

  readonly deltaPct = computed(() => {
    const line = this.line();
    if (!line.vendorValue) return 0;
    return Math.round(((this.displayValue() - line.vendorValue) / line.vendorValue) * 100);
  });

  /** True when the agent's suggestion is identical to the vendor's ask — there's nothing to
   * counter to, so "Use Counter" would just re-submit the vendor's own number. Compared before
   * any decision is made, so this reads suggestedValue directly rather than displayValue()
   * (which only resolves to suggestedValue once no decision/edit is in effect anyway). */
  readonly hasNoCounterToUse = computed(() => {
    const line = this.line();
    return line.counterThisLine && line.suggestedValue === line.vendorValue;
  });

  readonly deltaLabel = computed(() => {
    const pct = this.deltaPct();
    return pct === 0 ? 'no change' : (pct > 0 ? '+' : '') + pct + '%';
  });

  readonly deltaClass = computed<'is-down' | 'is-up' | 'is-flat'>(() => {
    const pct = this.deltaPct();
    return pct === 0 ? 'is-flat' : pct < 0 ? 'is-down' : 'is-up';
  });

  readonly itemLabel = computed(() => {
    const line = this.line();
    const base = line.itemName?.trim() || line.itemDescription?.trim() || line.itemCode?.trim() || 'Line item';
    // Display-only: show the charge type as a short bucket (Labor / Helper / Trip); any non-charge
    // name (e.g. a material) passes through unchanged. See charge-type-label.util.ts (Nahid, 2 Nov).
    const isLabor = line.isLabor === true || line.itemCode?.trim().toUpperCase() === 'LABOR';
    return chargeTypeDisplayLabel(base, isLabor);
  });

  /**
   * The line's work/material description (Nahid, 29 Oct): the counter strip must show the description
   * of the work (labor) or the material item description so the admin can judge the counter without
   * opening Info. Hidden when it is empty or when it would just repeat the item label already shown.
   */
  readonly descriptionText = computed(() => {
    const desc = this.line().itemDescription?.trim() ?? '';
    return desc && desc !== this.itemLabel() ? desc : '';
  });

  /**
   * Labor lines only (Nahid, 29 Oct): Lance must always see the two countered quantities — the crew
   * size (techs) and the per-tech hours — not just the rate. Each part shows the vendor's current
   * value struck through with the countered value beside it when it changed, else the plain value.
   * The countered side is the admin's own edit when they edited the line, otherwise the agent's
   * suggestion — but only while the line is actually being countered and not declined. Returns null
   * for non-labor lines or when neither a crew size nor hours is known.
   */
  readonly crewParts = computed<CrewPart[] | null>(() => {
    const line = this.line();
    // RFI's standard counter does not flag labor (isLabor), so the labor item code counts too.
    if (line.isLabor !== true && line.itemCode?.trim().toUpperCase() !== 'LABOR') return null;
    const decision = this.decision();
    const round = (n: number) => Math.round(n * 100) / 100;

    const curTechs = line.techs ?? null;
    const curHrs = line.hoursPerTech ?? null;

    // The counter side: the admin's edit if they edited, else the agent's suggestion. Only shown
    // while this line is being countered and not declined (a declined line keeps the vendor's ask).
    let ctrTechs: number | null = null;
    let ctrHrs: number | null = null;
    if (line.counterThisLine && decision !== 'decline') {
      if (decision === 'edit') {
        ctrTechs = this.editedTechs() ?? line.suggestedTechs ?? null;
        ctrHrs = this.editedHoursPerTech() ?? line.suggestedHoursPerTech ?? null;
      } else {
        ctrTechs = line.suggestedTechs ?? null;
        ctrHrs = line.suggestedHoursPerTech ?? null;
      }
    }

    if (curTechs == null && curHrs == null && ctrTechs == null && ctrHrs == null) return null;

    // The struck-through side is the vendor's saved ORIGINAL when there is one (the line's current values follow every vendor
    // edit); the plain value shown when nothing is countered stays the current one.
    const part = (
      label: string,
      cur: number | null,
      ctr: number | null,
      was: number | null,
      fmt: (n: number) => string,
    ): CrewPart | null => {
      if (cur == null && ctr == null) return null;
      const now = ctr ?? cur!;
      const struck = was ?? cur;
      const changed = ctr != null && struck != null && round(ctr) !== round(struck);
      return { label, orig: changed ? fmt(struck!) : null, now: fmt(now), unit: label === 'Techs' ? (Math.round(now) === 1 ? 'tech' : 'techs') : 'each' };
    };

    // The unit is shown after the pills ("5 -> 3 techs", "5 h -> 4.04 h each"), so the crew number carries none.
    const techsFmt = (n: number) => `${Math.round(n)}`;
    const hrsFmt = (n: number) => `${round(n)} h`;

    return [
      part('Techs', curTechs, ctrTechs, line.originalTechs ?? null, techsFmt),
      part('Hrs/tech', curHrs, ctrHrs, line.originalHoursPerTech ?? null, hrsFmt),
    ].filter((p): p is CrewPart => p != null);
  });

  readonly flagType = computed<'warn' | 'good'>(() => {
    const type = this.line().flags[0]?.type?.toLowerCase() ?? '';
    return type.includes('warn') || type.includes('risk') || type.includes('high') ? 'warn' : 'good';
  });

  readonly flagLabel = computed(() => {
    const line = this.line();
    const flag = line.flags[0];
    return (
      flag?.code?.trim() ||
      flag?.type?.trim() ||
      (line.deltaPct != null && line.deltaPct < 0 ? 'Counter advised' : 'Justified')
    );
  });

  readonly reasoning = computed(() => {
    const line = this.line();
    return (
      line.flags[0]?.detail?.trim() ||
      line.llmReasoning?.trim() ||
      line.reasoning?.trim() ||
      line.displaySuggestion?.trim() ||
      'No agent reasoning was returned for this line.'
    );
  });

  readonly hasReasoning = computed(() => this.reasoning().length > 0);

  readonly confidencePct = computed(() => {
    const confidence = this.line().confidence;
    if (confidence == null) return 0;
    const pct = confidence <= 1 ? confidence * 100 : confidence;
    return Math.max(0, Math.min(100, Math.round(pct)));
  });

  readonly reduction = computed(() => (this.line().vendorValue ?? 0) - (this.line().suggestedValue ?? 0));

  readonly stateLabel = computed(() => {
    switch (this.decision()) {
      case 'accept':
        return '✓ Counter in use';
      case 'edit':
        return '✎ Edited by user';
      case 'decline':
        return '✕ Charge declined ($0.00)';
      default:
        return 'Pending — will decline if unresolved';
    }
  });

  readonly stateClass = computed(() => {
    switch (this.decision()) {
      case 'accept':
        return 'negline-state--approved';
      case 'edit':
        return 'negline-state--edited';
      case 'decline':
        return 'negline-state--declined';
      default:
        return 'negline-state--pending';
    }
  });

  gaugeDashOffset(pct: number): number {
    return EstimateNegotiationLineComponent.GAUGE_RING_CIRCUMFERENCE * (1 - pct / 100);
  }

  isExpanded(mode: 'edit' | 'info' | 'msg'): boolean {
    return this.expandMode() === mode;
  }

  onDecisionClick(decision: 'accept' | 'edit'): void {
    if (this.disabled()) return;
    if (decision === 'edit') {
      this.toggleExpand('edit');
      return;
    }
    this.expandModeChange.emit(null);
    this.decisionChange.emit(decision);
  }

  onChangeClick(): void {
    if (this.disabled()) return;
    this.changeDecision.emit();
  }

  toggleInfo(event?: Event): void {
    event?.stopPropagation();
    this.toggleExpand('info');
  }

  toggleMessage(event?: Event): void {
    event?.stopPropagation();
    this.toggleExpand('msg');
  }

  private toggleExpand(mode: 'edit' | 'info' | 'msg'): void {
    if (this.isExpanded(mode)) {
      this.expandModeChange.emit(null);
      return;
    }
    if (mode === 'edit') {
      const line = this.line();
      const total = this.editedValue() ?? line.suggestedValue;
      const lockedRate = line.lockedRate;
      const noPriorEdit = this.editedValue() == null && this.editedQty() == null
        && this.editedTechs() == null && this.editedHoursPerTech() == null;

      if (this.isLaborEdit()) {
        // Labor edits two fields: crew size and per-tech hours. editQtyDraft holds per-tech hours;
        // man-hours (what the counter sends as qty) is techs × per-tech. Seed from a prior edit,
        // else the line's current split, else the agent's suggested split, else derive per-tech
        // from the suggested man-hours ÷ techs.
        // Shabbar, 30 Oct (PO 28103): with no edit of the admin's own, Edit opens on the COUNTER shown on the line (3 techs x 4.04 h,
        // $787.80), not on the line's stored split (which showed 4 h and a rate of $65.65 for the same total).
        const techs = this.editedTechs() ?? (noPriorEdit ? line.suggestedTechs : null) ?? line.techs ?? line.suggestedTechs ?? 1;
        const safeTechs = techs > 0 ? Math.round(techs) : 1;
        let perTech =
          this.editedHoursPerTech() ?? (noPriorEdit ? line.suggestedHoursPerTech : null) ?? line.hoursPerTech ?? line.suggestedHoursPerTech ?? null;
        if (perTech == null) {
          const manHours = this.editedQty() ?? line.suggestedQty ?? safeTechs;
          perTech = safeTechs > 0 && manHours > 0 ? manHours / safeTechs : 1;
        }
        const safePerTech = perTech > 0 ? perTech : 1;
        this.editTechsDraft.set(safeTechs);
        this.editQtyDraft.set(Math.round(safePerTech * 100) / 100);
        const manHours = safeTechs * safePerTech;
        const rate = noPriorEdit && lockedRate != null && lockedRate > 0
          ? lockedRate
          : manHours > 0 ? total / manHours : 0;
        this.editRateDraft.set(Math.round(rate * 100) / 100);
      } else {
        const qty = this.editedQty() ?? line.suggestedQty ?? 1;
        const safeQty = qty > 0 ? qty : 1;
        this.editTechsDraft.set(null);
        this.editQtyDraft.set(safeQty);
        // Prefer the vendor's locked rate-card rate over total/qty — that division drifts slightly
        // from the true rate due to qty rounding (e.g. $74.96 instead of an exact $75.00). Only
        // applies when there's no prior admin edit already driving total/qty.
        const rate = noPriorEdit && lockedRate != null && lockedRate > 0
          ? lockedRate
          : total / safeQty;
        this.editRateDraft.set(Math.round(rate * 100) / 100);
      }
    }
    this.expandModeChange.emit(mode);
  }

  readonly editCounterTotal = computed(() => {
    const rate = this.editRateDraft();
    if (rate == null || Number.isNaN(rate)) return null;
    if (this.isLaborEdit()) {
      const techs = this.editTechsDraft();
      const perTech = this.editQtyDraft();
      if (techs == null || perTech == null || Number.isNaN(techs) || Number.isNaN(perTech)) return null;
      return techs * perTech * rate;
    }
    const qty = this.editQtyDraft();
    if (qty == null || Number.isNaN(qty)) return null;
    return qty * rate;
  });

  setEditQtyDraft(value: number | string | null): void {
    this.editQtyDraft.set(value == null || value === '' ? null : +value);
  }

  setEditTechsDraft(value: number | string | null): void {
    this.editTechsDraft.set(value == null || value === '' ? null : +value);
  }

  setEditRateDraft(value: number | string | null): void {
    this.editRateDraft.set(value == null || value === '' ? null : +value);
  }

  confirmEdit(): void {
    const rate = this.editRateDraft();
    if (rate == null || Number.isNaN(rate) || rate < 0) return;
    const rate2 = Math.round(rate * 100) / 100;

    if (this.isLaborEdit()) {
      const techs = this.editTechsDraft();
      const perTech = this.editQtyDraft();
      if (techs == null || perTech == null || Number.isNaN(techs) || Number.isNaN(perTech) || techs < 1 || perTech < 0) {
        return;
      }
      // Crew size is a whole number; per-tech hours keep decimals (e.g. 1.5 h). The counter's qty
      // stays the cumulative man-hours (techs × per-tech), which is what the backend/agent expect.
      const techs2 = Math.round(techs);
      const perTech2 = Math.round(perTech * 100) / 100;
      const manHours = Math.round(techs2 * perTech2 * 100) / 100;
      this.editConfirm.emit({
        qty: manHours,
        rate: rate2,
        value: Math.round(manHours * rate2 * 100) / 100,
        techs: techs2,
        hoursPerTech: perTech2,
      });
      this.expandModeChange.emit(null);
      return;
    }

    const qty = this.editQtyDraft();
    if (qty == null || Number.isNaN(qty) || qty < 0) return;
    // Qty and rate keep decimals (e.g. 1.5 hrs, $12.75) — rounded to cents, never to whole numbers.
    const qty2 = Math.round(qty * 100) / 100;
    this.editConfirm.emit({ qty: qty2, rate: rate2, value: Math.round(qty2 * rate2 * 100) / 100 });
    this.expandModeChange.emit(null);
  }

  cancelEdit(): void {
    this.expandModeChange.emit(null);
  }

  onMessageInput(value: string): void {
    this.messageChange.emit(value);
  }

  onResetMessage(): void {
    this.messageReset.emit();
  }
}
